import { CreateLessonPracticeTask } from "./LessonPracticeReview.js";
import { SetAssetPracticeTask } from "../SetAssetPracticeTask.js";
import { ActionConfirmation } from "../ui/ActionConfirmation.js";
import { LessonNoteText } from "../LessonNoteText.js";
import { useRef, useState, type FormEvent, type MouseEventHandler } from "react";
import { PracticeTodoPanel } from "../calendar/PracticeTodoPanel.js";
import { formatDate, formatDuration, formatTime } from "../format.js";
import { shareGroove, type Groove } from "../grooves.js";
import { attachGroove, listLessonGrooves } from "../lessonGrooves.js";
import {
  addStaffPracticeTodo,
  getStaffPracticeTodos,
  type PracticeTodo,
} from "../practiceTodos.js";
import { addRepertoireEntry, getStaffRepertoire } from "../repertoire.js";
import { get } from "../api.js";
import { ConfirmDialog, Drawer } from "../ui/Dialog.js";
import { TutorMetronome } from "./TutorMetronome.js";
import { useAsync } from "../useAsync.js";
import { GrooveLibraryPicker } from "../GrooveLibraryPicker.js";
import { PracticeTaskGrooveChoice } from "../PracticeTaskGrooveChoice.js";
import { emptyPracticeTaskDraft } from "../practiceTaskDraft.js";
import { buildFormattedLessonNote, formatLessonNoteLocally, type LocalLessonNoteDraft } from "../lessonNoteFormatting.js";
import { LocalLessonNoteFormatter } from "./LocalLessonNoteFormatter.js";
import { PreviousLessonGrooveLinks } from "./PreviousLessonGrooves.js";
import { LessonRepertoirePicker } from "../LessonRepertoirePicker.js";
import { RepertoireSearchPicker } from "../RepertoirePicker.js";
import type { LessonResourceMatch } from "../LessonResourceEditor.js";
import type { RepertoireEntry, RepertoireLibraryPiece, LessonRepertoireSelection, LessonRepertoireSelectionResult } from "../repertoire.js";

type Tool = "practice" | "practice-groove" | "metronome" | "groove" | "repertoire" | "previous" | "format";

interface Props {
  studentId: string;
  studentName: string;
  currentStartsAt: string;
  lessonId: string | null;
  instrument: string | null;
  ensureNote?: () => Promise<string | null>;
  onPracticeChanged?: () => void;
  showPreviousPractice?: boolean;
  compactDisclosure?: boolean;
  onAddRepertoirePiece?: (title: string) => void;
  onAddRepertoireAsset?: (resource: LessonResourceMatch, key: string) => Promise<void>;
  onAssignRepertoire?: (selection: LessonRepertoireSelection, key: string) => Promise<LessonRepertoireSelectionResult>;
  onResourceBusyChange?: (busy: boolean) => void;
  localLessonNoteFormattingEnabled?: boolean;
  noteContent?: string;
  onApplyFormattedSummary?: (content: string) => void;
  lessonDraftSaved?: boolean;
  /** Copy only the authorized previous lesson's family-note text into this draft. */
  onCopyPreviousNote?: (content: string) => void;
  /** Published current notes must be withdrawn before copied text can be saved. */
  canCopyPreviousNote?: boolean;
  copyPreviousNoteDisabled?: boolean;
  disabled?: boolean;
}

export interface PreviousLessonAttachment {
  id: string;
  fileName: string;
  fileType: string | null;
  fileSize: number | null;
  resourceType: string | null;
  url: string;
}

export interface PreviousLessonContext {
  id: string;
  title: string;
  content: string | null;
  lessonDate: string;
  attendanceStatus: string | null;
  publishedAt: string | null;
  attachments: PreviousLessonAttachment[];
  editorHref?: string | null;
}

interface PreviousLessonResponse {
  previousLesson: PreviousLessonContext | null;
}

/**
 * Prefer the durable lesson link carried by metronome assignments. Only groove
 * tasks fall back to creation time because groove shares do not yet store a
 * source lesson id. A task explicitly linked to another lesson can therefore
 * never drift into this card just because it was created during the same week.
 */
export function practiceSincePreviousLesson(
  todos: readonly PracticeTodo[],
  previousLesson: PreviousLessonContext | null,
  currentStartsAt: string,
): PracticeTodo[] {
  if (!previousLesson) return [];

  const exact = todos.filter(
    (todo) => todo.metronome?.sourceLessonId === previousLesson.id,
  );
  const from = Date.parse(previousLesson.lessonDate);
  const to = Date.parse(currentStartsAt);
  const grooves = todos.filter((todo) => {
    if (!todo.groove || todo.metronome?.sourceLessonId) return false;
    const created = Date.parse(todo.createdAt);
    return Number.isFinite(created) && created >= from && created < to;
  });

  return [...new Map([...exact, ...grooves].map((todo) => [todo.id, todo])).values()];
}

async function getPreviousLessonContext(
  studentId: string,
  currentStartsAt: string,
): Promise<PreviousLessonContext | null> {
  const result = await get<PreviousLessonResponse>(
    `/admin/students/${encodeURIComponent(studentId)}/lesson-note-context`,
    { before: currentStartsAt },
  );
  return result.previousLesson;
}

export function LessonNotesTools({
  studentId,
  studentName,
  currentStartsAt,
  lessonId,
  instrument,
  ensureNote,
  onPracticeChanged,
  showPreviousPractice = true,
  onAddRepertoirePiece,
  onAddRepertoireAsset,
  onAssignRepertoire,
  onResourceBusyChange,
  onCopyPreviousNote,
  canCopyPreviousNote,
  copyPreviousNoteDisabled,
  compactDisclosure = false,
  localLessonNoteFormattingEnabled = false,
  noteContent = "",
  onApplyFormattedSummary,
  lessonDraftSaved = false,
  disabled = false,
}: Props) {
  const [open, setOpen] = useState<Tool | null>(null);
  const [practiceBusy, setPracticeBusy] = useState(false);
  const [practiceDraft, setPracticeDraft] = useState(emptyPracticeTaskDraft);
  const [formatDraft, setFormatDraft] = useState<LocalLessonNoteDraft | null>(null);
  const [pendingPracticeTask, setPendingPracticeTask] = useState(false);
  const [returnToFormatter, setReturnToFormatter] = useState(false);
  const formatterAvailable = localLessonNoteFormattingEnabled && noteContent.trim().length > 0 && Boolean(onApplyFormattedSummary);
  const todos = useAsync(() => getStaffPracticeTodos(studentId), [studentId], {
    reloadOnMirrorRefresh: true,
  });
  const repertoire = useAsync(() => getStaffRepertoire(studentId), [studentId]);
  const previous = useAsync(
    () => getPreviousLessonContext(studentId, currentStartsAt),
    [studentId, currentStartsAt],
    { reloadOnMirrorRefresh: true },
  );
  const previousTasks = practiceSincePreviousLesson(
    todos.data ?? [],
    previous.data ?? null,
    currentStartsAt,
  );
  const toolButtons = <nav className="lesson-note-tool-rail" aria-label="Lesson teaching tools">
    <ToolButton icon="✓" label="Practice task" disabled={disabled || (pendingPracticeTask && !lessonDraftSaved)} onClick={(event) => { event.currentTarget.focus(); setReturnToFormatter(false); if (!pendingPracticeTask) setPracticeDraft(emptyPracticeTaskDraft()); setOpen("practice"); }} />
    {formatterAvailable && <ToolButton icon="✎" label="Format notes locally" disabled={disabled} onClick={() => { setFormatDraft(formatLessonNoteLocally(noteContent)); setOpen("format"); }} />}
    <ToolButton icon="♩" label="Metronome" onClick={(event) => { event.currentTarget.focus(); setOpen("metronome"); }} />
    <ToolButton icon="♫" label="Drum groove" onClick={(event) => { event.currentTarget.focus(); setOpen("groove"); }} />
    <ToolButton icon="▤" label="Repertoire" onClick={() => setOpen("repertoire")} />
    <ToolButton icon="↶" label="Previous lesson" onClick={() => setOpen("previous")} />
  </nav>;

  return (
    <>
      {compactDisclosure ? (
        <details className="private-writeup-disclosure private-writeup-tools">
          <summary>Teaching tools <span className="private-writeup-disclosure-hint small muted">{formatterAvailable ? "6" : "5"} tools</span></summary>
          {toolButtons}
        </details>
      ) : toolButtons}

      {pendingPracticeTask && <section className="panel stack">
        <p className="small" role="status">A practice suggestion is ready. Save the reviewed lesson draft before creating the student task. Saving an unpublished lesson keeps it private; saving an already published lesson follows its current visibility.</p>
        <div className="row">
          <button type="button" disabled={disabled || !lessonDraftSaved} onClick={() => { setReturnToFormatter(false); setOpen("practice"); }}>Continue to practice task</button>
          <button type="button" className="quiet" disabled={disabled} onClick={() => { setPendingPracticeTask(false); setPracticeDraft(emptyPracticeTaskDraft()); }}>Cancel</button>
        </div>
      </section>}

      {showPreviousPractice && <PreviousPracticeCard
        practiceLoading={todos.loading}
        practiceError={todos.error}
        previousLoading={previous.loading}
        previousError={previous.error}
        previousLesson={previous.data ?? null}
        tasks={previousTasks}
        onRetryPractice={todos.reload}
        onRetryPrevious={previous.reload}
        onOpenPrevious={() => setOpen("previous")}
        onCopyPreviousNote={onCopyPreviousNote}
        canCopyPreviousNote={canCopyPreviousNote}
        copyPreviousNoteDisabled={copyPreviousNoteDisabled}
      />}

      {open === "practice" && ensureNote && <CreateLessonPracticeTask domain="private" studentId={studentId} pupilName={studentName}
        ensureNote={ensureNote} draft={practiceDraft} onDraftChange={setPracticeDraft} onChooseGroove={() => setOpen("practice-groove")} onClose={() => { setOpen(returnToFormatter ? "format" : null); setReturnToFormatter(false); }} onSaved={() => { todos.reload(); onPracticeChanged?.(); setPendingPracticeTask(false); }} />}
      {open === "format" && formatDraft && onApplyFormattedSummary && <LocalLessonNoteFormatter
        sourceContent={noteContent}
        draft={formatDraft}
        onDraftChange={setFormatDraft}
        onApplySummary={(formatted) => { onApplyFormattedSummary(formatted); setOpen(null); }}
        onCreatePracticeTask={(suggestion) => {
          const cleanSuggestion = suggestion.trim();
          const longSuggestion = cleanSuggestion.length > 200;
          setPracticeDraft({
            ...emptyPracticeTaskDraft(),
            title: longSuggestion ? "Practice from lesson notes" : cleanSuggestion,
            description: longSuggestion ? cleanSuggestion : "",
          });
          setPendingPracticeTask(true);
          onApplyFormattedSummary(buildFormattedLessonNote(formatDraft));
          setReturnToFormatter(false);
          setOpen(null);
        }}
        onClose={() => setOpen(null)}
        disabled={disabled}
      />}
      {open === "practice" && !ensureNote && (
        <Drawer
          title="Practice task"
          eyebrow={studentName}
          description="Set something to practise before the next lesson. It appears on the student's Practice page straight away, with a timer and a tick box."
          onClose={() => {
            if (practiceBusy) return;
            setOpen(null);
            todos.reload();
          }}
          size="medium"
          busy={practiceBusy}
        >
          <PracticeTaskGrooveChoice disabled={practiceBusy} onChoose={() => setOpen("practice-groove")} />
          <PracticeTodoPanel
            studentId={studentId}
            studentName={studentName}
            onBusyChange={setPracticeBusy}
            draft={practiceDraft}
            onDraftChange={setPracticeDraft}
          />
        </Drawer>
      )}
      {open === "metronome" && (
        <MetronomeAssignmentDrawer
          key={studentId}
          studentId={studentId}
          studentName={studentName}
          lessonId={lessonId}
          onClose={() => setOpen(null)}
          onSaved={() => { todos.reload(); return onPracticeChanged?.(); }}
        />
      )}
      {(open === "groove" || open === "practice-groove") && (
        <GrooveAssignmentDrawer
          studentId={studentId}
          studentName={studentName}
          lessonId={lessonId}
          onClose={() => setOpen(open === "practice-groove" ? "practice" : null)}
          onSaved={() => { todos.reload(); onPracticeChanged?.(); }}
        />
      )}
      {open === "repertoire" && (
        <RepertoireDrawer
          key={studentId}
          studentId={studentId}
          studentName={studentName}
          instrument={instrument}
          current={repertoire.data?.current ?? []}
          loading={repertoire.loading}
          error={repertoire.error}
          onRetry={repertoire.reload}
          onAddPiece={onAddRepertoirePiece}
          onAddAsset={onAddRepertoireAsset}
          onAssign={onAssignRepertoire}
          onResourceBusyChange={onResourceBusyChange}
          disabled={disabled}
          onClose={() => setOpen(null)}
          onSaved={() => { repertoire.reload(); todos.reload(); onPracticeChanged?.(); }}
        />
      )}
      {open === "previous" && (
        <PreviousLessonDrawer
          studentId={studentId}
          onPracticeChanged={() => { todos.reload(); onPracticeChanged?.(); }}
          lesson={previous.data ?? null}
          loading={previous.loading}
          error={previous.error}
          studentName={studentName}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}

function ToolButton({ icon, label, onClick, disabled = false }: { icon: string; label: string; onClick: MouseEventHandler<HTMLButtonElement>; disabled?: boolean }) {
  return (
    <button type="button" className="lesson-note-tool-button" onClick={onClick} disabled={disabled}>
      <span className="lesson-note-tool-icon" aria-hidden="true">{icon}</span>
      <span>{label}</span>
    </button>
  );
}

function PreviousPracticeCard({
  practiceLoading,
  practiceError,
  previousLoading,
  previousError,
  previousLesson,
  tasks,
  onRetryPractice,
  onRetryPrevious,
  onOpenPrevious,
  onCopyPreviousNote,
  canCopyPreviousNote = false,
  copyPreviousNoteDisabled = false,
}: {
  practiceLoading: boolean;
  practiceError: string | null;
  previousLoading: boolean;
  previousError: string | null;
  previousLesson: PreviousLessonContext | null;
  tasks: PracticeTodo[];
  onRetryPractice: () => void;
  onRetryPrevious: () => void;
  onOpenPrevious: () => void;
  onCopyPreviousNote?: (content: string) => void;
  canCopyPreviousNote?: boolean;
  copyPreviousNoteDisabled?: boolean;
}) {
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [copiedLessonId, setCopiedLessonId] = useState<string | null>(null);
  const noteText = previousLesson?.content?.trim() ?? "";
  const notePreview = noteText.length > 360 ? `${noteText.slice(0, 360)}…` : noteText;
  const copied = previousLesson !== null && copiedLessonId === previousLesson.id;

  function copyPreviousNote() {
    if (!previousLesson || !noteText || !onCopyPreviousNote || !canCopyPreviousNote || copyPreviousNoteDisabled) return;
    onCopyPreviousNote(noteText);
    setCopiedLessonId(previousLesson.id);
    setConfirmationOpen(false);
  }

  return (
    <section className="panel lesson-note-previous-practice" aria-labelledby="previous-practice-heading">
      <div className="lesson-note-section-heading">
        <div>
          <h2 id="previous-practice-heading">Previous lesson context</h2>
          <p className="small muted">Review the last lesson note and practice logged since then.</p>
        </div>
        {!previousLoading && !previousError && previousLesson && (
          <span className="small muted">Since {formatDate(previousLesson.lessonDate)}</span>
        )}
      </div>
      {previousLoading && <p className="empty">Loading previous lesson context…</p>}
      {previousError && (
        <div className="small muted">
          <p>Previous lesson context could not be refreshed.</p>
          <button type="button" className="quiet small" onClick={onRetryPrevious}>Retry previous context</button>
        </div>
      )}
      {!previousLoading && !previousError && !previousLesson && (
        <p className="empty">No previous written lesson was found.</p>
      )}
      {!previousLoading && !previousError && previousLesson && (
        <div className="lesson-note-previous-summary">
          <div className="lesson-note-section-heading">
            <div>
              <h3>{previousLesson.title || "Previous lesson note"}</h3>
              <p className="small muted">{previousLesson.publishedAt ? "Published note" : "Staff-only draft"}</p>
            </div>
            <button type="button" className="quiet small" onClick={onOpenPrevious}>Read full note</button>
          </div>
          {notePreview ? (
            <p className="small">{notePreview}</p>
          ) : (
            <p className="small muted">No written note is available for this lesson.</p>
          )}
        </div>
      )}
      {previousLesson && noteText && onCopyPreviousNote && (
        <div className="lesson-note-copy-previous">
          {canCopyPreviousNote ? (
            <>
              <button
                type="button"
                className="quiet small"
                disabled={copyPreviousNoteDisabled || copied}
                onClick={() => {
                  if (previousLesson.publishedAt) copyPreviousNote();
                  else setConfirmationOpen(true);
                }}
              >
                {copied ? "Added to this draft" : "Add previous note to draft"}
              </button>
              {copied && (
                <p className="small muted" role="status">
                  Added to this lesson's draft. Review it before saving or publishing.
                </p>
              )}
              {!previousLesson.publishedAt && !copied && (
                <p className="small muted">
                  This earlier note is unpublished. Only its family-note text will be copied, and the current note will remain unsaved and unpublished.
                </p>
              )}
            </>
          ) : (
            <p className="small muted">
              Withdraw this published lesson note to a draft before copying earlier note text. Saving a published note keeps it visible to family.
            </p>
          )}
          {confirmationOpen && !previousLesson.publishedAt && (
            <ConfirmDialog
              title="Copy the previous draft note?"
              description="Only the previous lesson's family-note text will be added to this editable draft. Nothing is saved or published."
              confirmLabel="Copy into draft"
              onConfirm={copyPreviousNote}
              onClose={() => setConfirmationOpen(false)}
              busy={copyPreviousNoteDisabled}
            />
          )}
        </div>
      )}
      {practiceLoading && <p className="empty">Loading practice progress…</p>}
      {practiceError && (
        <div className="small muted">
          <p>Practice progress could not be refreshed.</p>
          <button type="button" className="quiet small" onClick={onRetryPractice}>Retry practice progress</button>
        </div>
      )}
      {!practiceLoading && !practiceError && !previousLoading && !previousError && previousLesson && tasks.length === 0 && (
        <p className="empty">No practice tasks were set from the previous lesson.</p>
      )}
      {!practiceLoading && !practiceError && !previousLoading && !previousError && previousLesson && tasks.length > 0 && (
        <div className="lesson-note-practice-list">
          {tasks.map((todo) => (
            <div className="lesson-note-practice-row" key={todo.id}>
              <div>
                <strong>{todo.title}</strong>
                <div className="small muted">
                  {todo.metronome && `${todo.metronome.bpm} BPM · ${todo.metronome.signature}`}
                  {todo.groove && `${todo.metronome ? " · " : ""}Groove: ${todo.groove.title}`}
                  {todo.repertoireTitle && `${todo.metronome || todo.groove ? " · " : ""}${todo.repertoireTitle}`}
                </div>
              </div>
              <div className="lesson-note-practice-progress">
                <strong>{formatDuration(todo.practiceSeconds)}</strong>
                <span className="small muted">logged</span>
              </div>
              <span className={`tag ${todo.completed ? "good" : "warn"}`}>
                {todo.completed ? "Done" : "In progress"}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function MetronomeAssignmentDrawer({
  studentId,
  studentName,
  lessonId,
  onClose,
  onSaved,
}: {
  studentId: string;
  studentName: string;
  lessonId: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  return (
    <TutorMetronome
      onClose={onClose}
      assignment={{
        learnerName: studentName,
        domain: "private",
        onSave: async (task) => {
          if (!lessonId) {
            throw new Error("Save the lesson note as a draft first so this preset can stay attached to this lesson.");
          }
          await addStaffPracticeTodo(studentId, {
            ...task,
            metronome: { ...task.metronome, sourceLessonId: lessonId },
          });
        },
        onSaved,
      }}
    />
  );
}

function GrooveAssignmentDrawer({
  studentId,
  studentName,
  lessonId,
  onClose,
  onSaved,
}: {
  studentId: string;
  studentName: string;
  lessonId: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [chosen, setChosen] = useState<Groove | null>(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function save() {
    if (!chosen) return;
    setSaving(true);
    setProblem(null);
    try {
      await shareGroove(chosen.id, studentId, notes.trim() || undefined, true);
      if (lessonId) {
        try {
          await attachGroove(lessonId, chosen.id);
        } catch {
          // The practice task is already durable. A failed note attachment can
          // still be repaired with the existing groove picker without losing it.
        }
      }
      setDone(true);
      onSaved();
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : "Could not set that groove as practice.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Drawer
      title="Drum groove"
      eyebrow={studentName}
      description="Choose from the Groove library, A–Z. The student receives the saved snapshot as a playable practice task."
      onClose={onClose}
      size="medium"
      busy={saving}
    >
      {done ? (
        <ActionConfirmation variant="success" title="Groove assigned" description={<> The student can open and play the exact groove you set. </>} action={<button type="button" onClick={onClose}>Done</button>} />
      ) : (
        <div className="lesson-tool-form">
          {problem && <div className="error">{problem}</div>}
          <GrooveLibraryPicker chosen={chosen} onChange={setChosen} disabled={saving} />
          <label className="lesson-tool-field">
            <span>Practice notes shown to the student</span>
            <textarea rows={5} value={notes} disabled={saving} onChange={(event) => setNotes(event.target.value)} placeholder="E.g. Play slowly first, then build the tempo while keeping the hi-hat even." />
          </label>
          <button type="button" disabled={saving || !chosen} onClick={() => void save()}>{saving ? "Assigning…" : "Add to practice tasks"}</button>
        </div>
      )}
    </Drawer>
  );
}

function RepertoireDrawer({
  studentId,
  studentName,
  instrument,
  current,
  onClose,
  onSaved,
  loading, error, onRetry, onAddPiece, onAddAsset, onAssign, onResourceBusyChange, disabled,
}: {
  studentId: string;
  studentName: string;
  instrument: string | null;
  current: RepertoireEntry[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onAddPiece?: (title: string) => void;
  onAddAsset?: (resource: LessonResourceMatch, key: string) => Promise<void>;
  onAssign?: (selection: LessonRepertoireSelection, key: string) => Promise<LessonRepertoireSelectionResult>;
  onResourceBusyChange?: (busy: boolean) => void;
  disabled: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState("");
  const [composer, setComposer] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [assetBusy, setAssetBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [selectionRequest, setSelectionRequest] = useState<{ entryId: string }>();
  const createLock = useRef(false);

  async function addLibraryPiece(piece: RepertoireLibraryPiece) {
    if (createLock.current || assetBusy || disabled || loading || error) return;
    const existing = current.find(entry => entry.libraryPieceId === piece.id);
    if (existing) { setSelectionRequest({ entryId: existing.id }); return; }
    createLock.current = true;
    setSaving(true);
    setProblem(null);
    try {
      const entry = await addRepertoireEntry(studentId, {
        libraryPieceId: piece.id, title: piece.title, composer: piece.composer,
        workOrCollection: piece.workOrCollection, instrument: instrument ?? piece.instrument,
        status: "learning", familyVisible: true,
      });
      setSelectionRequest({ entryId: entry.id });
      onSaved();
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : "Could not add that repertoire library piece.");
    } finally {
      createLock.current = false;
      setSaving(false);
    }
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!title.trim() || createLock.current || assetBusy || disabled) return;
    createLock.current = true;
    setSaving(true);
    setProblem(null);
    try {
      await addRepertoireEntry(studentId, {
        title: title.trim(),
        composer: composer.trim() || null,
        instrument,
        status: "learning",
        tutorNote: note.trim() || null,
        familyVisible: true,
      });
      setTitle("");
      setComposer("");
      setNote("");
      onSaved();
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : "Could not add that repertoire.");
    } finally {
      createLock.current = false;
      setSaving(false);
    }
  }

  return (
    <Drawer title="Repertoire" eyebrow={studentName} description="Search the shared library or this student's current repertoire, then choose the assets to share or set as practice." onClose={onClose} size="medium" busy={saving || assetBusy}>
      <section className="lesson-repertoire-picker lesson-repertoire-library" aria-label="Add from the repertoire library">
        <h3>Add from the repertoire library</h3>
        <p className="small muted">Choose a song to add it to {studentName}&rsquo;s repertoire and show its individual assets. Sharing assets and setting practice are separate choices below.</p>
        <RepertoireSearchPicker label="Search repertoire library" disabled={saving || assetBusy || disabled || loading || Boolean(error)} onSelect={piece => void addLibraryPiece(piece)} />
        {saving && <p role="status">Adding repertoire…</p>}
        {problem && <div className="error" role="alert">{problem}</div>}
      </section>
      {onAddPiece && onAddAsset && <LessonRepertoirePicker studentId={studentId} current={current} loading={loading} error={error} onRetry={onRetry}
        onAddPiece={onAddPiece} onAddAsset={onAddAsset} onAssign={onAssign} onPracticeChanged={onSaved} disabled={disabled || saving}
        selectionRequest={selectionRequest}
        onBusyChange={(value) => { setAssetBusy(value); onResourceBusyChange?.(value); }} />}
      <details className="lesson-tool-current-list">
      <summary>Add a new repertoire piece</summary>
      <form className="lesson-tool-form" onSubmit={save}>
        <label className="lesson-tool-field"><span>Piece or song</span><input required value={title} onChange={(event) => setTitle(event.target.value)} /></label>
        <label className="lesson-tool-field"><span>Artist / composer</span><input value={composer} onChange={(event) => setComposer(event.target.value)} /></label>
        <label className="lesson-tool-field"><span>Tutor note</span><textarea rows={4} value={note} onChange={(event) => setNote(event.target.value)} /></label>
        <button type="submit" disabled={saving || assetBusy || disabled || !title.trim()}>{saving ? "Adding…" : "Add to repertoire"}</button>
      </form>
      </details>
    </Drawer>
  );
}

function PreviousLessonDrawer({
  studentId,
  onPracticeChanged,
  lesson,
  loading,
  error,
  studentName,
  onClose,
}: {
  studentId: string;
  onPracticeChanged: () => void;
  lesson: PreviousLessonContext | null;
  loading: boolean;
  error: string | null;
  studentName: string;
  onClose: () => void;
}) {
  return (
    <Drawer
      title="Previous lesson"
      eyebrow={studentName}
      description={lesson ? `${formatDate(lesson.lessonDate)} at ${formatTime(lesson.lessonDate)}` : undefined}
      onClose={onClose}
      size="large"
    >
      {loading && <p className="empty">Loading previous notes…</p>}
      {error && <div className="error">{error}</div>}
      {!loading && !error && !lesson && <p className="empty">No previous written lesson is available.</p>}
      {lesson && <PreviousLessonContent key={lesson.id} lesson={lesson} studentId={studentId} onPracticeChanged={onPracticeChanged} />}
    </Drawer>
  );
}

function PreviousLessonContent({ lesson, studentId, onPracticeChanged }: { lesson: PreviousLessonContext; studentId: string; onPracticeChanged: () => void }) {
  const grooves = useAsync(() => listLessonGrooves(lesson.id), [lesson.id]);

  return (
    <>
      <article className="lesson-tool-previous-note">
        <div className="spread">
          <h3>{lesson.title}</h3>
          <span className="tag">{lesson.attendanceStatus ?? "Attendance not recorded"}</span>
        </div>
        {lesson.content ? <p className="notes"><LessonNoteText content={lesson.content} /></p> : <p className="empty">No written detail.</p>}
      </article>

      {lesson.attachments.length > 0 && (
        <section className="lesson-tool-previous-attachments" aria-labelledby="previous-files-heading">
          <h3 id="previous-files-heading">Attachments</h3>
          {lesson.attachments.map((attachment) => (
            <div key={attachment.id}>
              <LessonAttachment attachment={attachment} />
              <SetAssetPracticeTask target={{ domain: "private", learnerId: studentId }} source={{ kind: "lesson_file", noteId: lesson.id, id: attachment.id }}
                assetTitle={attachment.fileName} onSaved={onPracticeChanged} />
            </div>
          ))}
        </section>
      )}

      {grooves.loading && <p className="small muted">Loading attached music…</p>}
      {grooves.error && <p className="small muted">Attached music could not be loaded.</p>}
      {grooves.data && (
        <PreviousLessonGrooveLinks grooves={grooves.data.grooves} headingId="previous-music-heading"
          practiceTarget={{ domain: "private", learnerId: studentId }} noteId={lesson.id} onPracticeChanged={onPracticeChanged} />
      )}
    </>
  );
}

function LessonAttachment({ attachment }: { attachment: PreviousLessonAttachment }) {
  const type = attachment.fileType ?? "";
  if (type.startsWith("image/")) {
    return (
      <figure className="lesson-tool-file-attachment">
        <img src={attachment.url} alt={attachment.fileName} loading="lazy" />
        <figcaption className="small muted">{attachment.fileName}</figcaption>
      </figure>
    );
  }
  if (type.startsWith("video/")) {
    return (
      <div className="lesson-tool-file-attachment">
        <video controls preload="metadata" src={attachment.url} aria-label={attachment.fileName} />
        <span className="small muted">{attachment.fileName}</span>
      </div>
    );
  }
  if (type.startsWith("audio/")) {
    return (
      <div className="lesson-tool-file-attachment">
        <strong>{attachment.fileName}</strong>
        <audio controls preload="metadata" src={attachment.url} />
      </div>
    );
  }
  return (
    <div className="lesson-tool-file-link">
      <a href={attachment.url} target="_blank" rel="noreferrer">Open {attachment.fileName}</a>
      {attachment.fileType && <span className="small muted">{attachment.fileType}</span>}
    </div>
  );
}
